import { ChangeDetectionStrategy, Component, inject, input, output, viewChild } from '@angular/core';
import { CreateArTranPayload, CreditArTranType } from '@anduin/api';
import { MessageService } from '@anduin/shared';
import { firstValueFrom } from 'rxjs';
import { ArTranApiService } from 'src/app/api/services/ar-tran-api.service';
import {
  NEW_TRANSACTION_DIALOG_MODE,
  NewTransactionComponent,
} from 'src/app/common/accounts-receivable/components/new-transaction/new-transaction.component';
import { IconComponent } from 'src/app/shared/components/icon/icon.component';
import { DrawerService } from 'src/app/shared/components/side-drawer/side-drawer.service';
import { ButtonDirective } from 'src/app/shared/directives/button.directive';
import { DataSelectorDirective } from 'src/app/shared/directives/data-selector.directive';

@Component({
  selector: 'app-create-payment-drawer',
  templateUrl: './create-payment-drawer.component.html',
  styleUrls: ['./create-payment-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NewTransactionComponent, IconComponent, ButtonDirective, DataSelectorDirective],
  providers: [{ provide: NEW_TRANSACTION_DIALOG_MODE, useValue: true }],
})
export class CreatePaymentDrawerComponent {
  private readonly arTranApi = inject(ArTranApiService);
  private readonly messageService = inject(MessageService);
  private readonly drawerService = inject(DrawerService);

  readonly invoiceId = input.required<number>();
  readonly clientId = input.required<number>();
  readonly outstandingAmount = input.required<number>();
  readonly currencyCode = input<string | undefined>();
  readonly invoiceNumber = input<string>();

  readonly paymentCreated = output<void>();

  readonly newTransaction = viewChild(NewTransactionComponent);

  protected readonly CreditArTranType = CreditArTranType;

  private latestPayload: CreateArTranPayload | undefined;

  onTransactionChange(payload: CreateArTranPayload): void {
    this.latestPayload = payload;
  }

  async save(): Promise<void> {
    const txComponent = this.newTransaction();
    if (!txComponent) return;

    const isValid = await firstValueFrom(txComponent.validate());
    if (!isValid || !this.latestPayload) return;

    try {
      await firstValueFrom(this.arTranApi.createArTran(this.latestPayload));
      this.messageService.success('Payment created successfully');
      this.paymentCreated.emit();
      this.drawerService.close();
    } catch {
      this.messageService.error('Failed to create payment');
    }
  }

  cancel(): void {
    this.drawerService.close();
  }
}
